import { Component, Input, OnInit, OnChanges, SimpleChanges, AfterViewInit, OnDestroy, signal, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { CheckboxModule } from 'primeng/checkbox';
import { CardModule } from 'primeng/card';
import { DividerModule } from 'primeng/divider';
import { ButtonModule } from 'primeng/button';
import { BillingInformation, OrderSummary } from '../../../models/psychometric-test/psychometric-test.model';
import { Store } from '@ngxs/store';
import { AuthService } from '../../../services/account/auth.service';
import { UserSelectors } from '../../../states/user/user-selector';
import { UserStateModel } from '../../../states/user/user-state-model';
import { UserResponse } from '../../../models/account/userResponse';
import { CreatePayNowRequest } from '../../../models/payment/createPayNowRequest';
import { PaymentService } from '../../../services/payments/payment/payment.service';
import { PaymentGatewayResponse } from '../../../models/payment/paymentGatewayResponse.model';
import { MessageService } from 'primeng/api';

@Component({
  selector: 'app-billing',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, InputTextModule, CheckboxModule, CardModule, DividerModule, ButtonModule],
  templateUrl: './billing.component.html',
  styleUrls: ['./billing.component.scss']
})
export class BillingComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @Input() orderSummary: OrderSummary | null = null;

  private paymentService = inject(PaymentService);
  private messageService = inject(MessageService);

  billingForm!: FormGroup;
  formSubmitted = false;
  processingPayment = false;
  userDetails = signal<UserResponse | null>(null);
  private dataCheckInterval?: any;
  private dataCheckCount = 0;
  private readonly MAX_CHECKS = 50;
  private retryTimeout?: any;

  constructor(
    private fb: FormBuilder,
    private store: Store,
    private authService: AuthService
  ) { }

  ngOnInit(): void {
    this.billingForm = this.fb.group({
      fullName: ['', [Validators.required, Validators.minLength(4), Validators.pattern(/^[a-zA-Z\s]+$/)]],
      emailAddress: ['', [Validators.required, Validators.email, Validators.pattern(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/)]],
      billingAddress: ['', [Validators.required, Validators.minLength(10)]],
      phoneNumber: ['', [Validators.required, Validators.pattern(/^[0-9]{10}$/), Validators.minLength(10), Validators.maxLength(10)]],
      agreeToTerms: [false, Validators.requiredTrue]
    });
    this.loadUserInfo();
  }

  ngAfterViewInit(): void {
    // setTimeout(() => this.startDataCheck(), 200);
  }

  ngOnDestroy(): void {
    this.stopDataCheck();
    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout);
      this.retryTimeout = undefined;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.billingForm && changes['orderSummary']) {
      setTimeout(() => {
        this.prefillUserData();
        if (this.hasAllUserData()) this.stopDataCheck();
      }, 0);
    }
  }

  private startDataCheck(): void {
    this.prefillUserData();
    if (this.hasAllUserData()) return;

    this.dataCheckCount = 0;
    this.dataCheckInterval = setInterval(() => {
      this.dataCheckCount++;
      this.prefillUserData();

      if (this.hasAllUserData() || this.dataCheckCount >= this.MAX_CHECKS) {
        this.stopDataCheck();
      }
    }, 100);
  }

  private stopDataCheck(): void {
    if (this.dataCheckInterval) {
      clearInterval(this.dataCheckInterval);
      this.dataCheckInterval = undefined;
      this.dataCheckCount = 0;
    }
  }

  private loadUserInfo(): void {
    this.store.selectOnce(UserSelectors.getUser).subscribe((user: UserStateModel) => {
      if (user?.id) {
        this.loadUserDetailsById(user.id);
      } else {
        // User not available yet, retry after 2 seconds
        this.retryTimeout = setTimeout(() => {
          this.retryLoadUserInfo();
        }, 2000);
      }
    });
  }

  private retryLoadUserInfo(): void {
    // Only retry if we still don't have user details
    if (this.userDetails()) {
      return;
    }

    this.store.selectOnce(UserSelectors.getUser).subscribe((user: UserStateModel) => {
      if (user?.id) {
        this.loadUserDetailsById(user.id);
      }
    });
  }

  private loadUserDetailsById(userId: string): void {
    this.authService.getUserByid(userId).subscribe({
      next: (userDetails: UserResponse) => {
        this.userDetails.set(userDetails);
        this.prefillUserData();
        this.stopDataCheck();
        // Clear retry timeout if it exists
        if (this.retryTimeout) {
          clearTimeout(this.retryTimeout);
          this.retryTimeout = undefined;
        }
      },
      error: () => {
        this.userDetails.set(null);
        this.stopDataCheck();
      }
    });
  }

  private hasAllUserData(): boolean {
    const user = this.userDetails();
    return !!(user?.firstName?.trim() && user?.email?.trim());
  }

  prefillUserData(): void {
    if (!this.billingForm) return;

    const user = this.userDetails();
    if (!user) return;

    const updates: Partial<BillingInformation> = {};

    const userFullName = `${user.firstName} ${user.lastName || ''}`.trim();
    if (userFullName && this.billingForm.get('fullName')?.value !== userFullName) {
      updates.fullName = userFullName;
    }

    if (user.email?.trim() && this.billingForm.get('emailAddress')?.value !== user.email.trim()) {
      updates.emailAddress = user.email.trim();
    }

    if (user.phoneNumber?.trim()) {
      const cleanPhone = user.phoneNumber.replace(/\D/g, '').substring(0, 10);
      if (cleanPhone.length === 10 && this.billingForm.get('phoneNumber')?.value !== cleanPhone) {
        updates.phoneNumber = cleanPhone;
      }
    }

    if (Object.keys(updates).length > 0) {
      this.billingForm.patchValue(updates, { emitEvent: false });
    }
  }

  formatPhoneNumber(event: any): void {
    const value = event.target.value.replace(/\D/g, '').substring(0, 10);
    event.target.value = value;
    this.billingForm.patchValue({ phoneNumber: value }, { emitEvent: false });
  }

  formatNameInput(event: any, fieldName: string): void {
    const value = event.target.value.replace(/[^a-zA-Z\s]/g, '');
    event.target.value = value;
    this.billingForm.patchValue({ [fieldName]: value }, { emitEvent: false });
  }

  onSubmit(): void {
    this.formSubmitted = true;
    this.billingForm.markAllAsTouched();
    if (this.billingForm.valid) {
      const payNowRequest = this.getPayNowRequestValue();
      if (!payNowRequest) {
        this.showError('Invalid payment request. Please check your information.');
        return;
      }

      this.processingPayment = true;
      this.paymentService.payNow(payNowRequest).subscribe({
        next: (response: PaymentGatewayResponse) => {
          if (response.redirectUri && response.redirectUri.trim() !== '') {
            // Redirect to payment gateway
            window.location.href = response.redirectUri;
          } else {
            // Show error message
            const errorMessage = response.responseDescription || 'Payment gateway error occurred. Please try again.';
            this.showError(errorMessage);
          }
        },
        error: (error: any) => {
          this.processingPayment = false;
          const errorMessage = error?.message || 'Failed to process payment. Please try again.';
          this.showError(errorMessage);
        }
      });
    }
  }

  private showError(message: string): void {
    this.messageService.add({
      severity: 'error',
      summary: 'Error',
      detail: message
    });
  }

  getPayNowRequestValue(): CreatePayNowRequest | undefined {
    const user = this.userDetails();

    return {
      name: this.billingForm.value.fullName,
      userEmail: this.billingForm.value.emailAddress,
      phoneNumber: this.billingForm.value.phoneNumber,
      billingAddress: {
        userId: user?.id,
        billAddress: this.billingForm.value.billingAddress
      },
      amount: this.orderSummary?.total || 0,
      transactionType: 'SALE',
      packageId: this.orderSummary?.packageId || 0,
      sourceUrl: window.location.origin + '/pay-success'
    };
  }
}
